import {
  Component,
  effect,
  input,
  InputSignal,
  OnDestroy,
  OnInit,
  signal,
  WritableSignal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  AbstractControl,
  ControlContainer,
  FormGroupDirective,
  NgControl,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { fromEvent, map, startWith, tap } from 'rxjs';
import { ErrorMessageComponent } from '../../../layout/error-message/error-message.component';
import {
  FormInputSelectComponent,
  IObjectOption,
  SelectOption,
} from '../form-input-select.component';
import { ToSelectOptionAttributePipe } from '../../../../pipes/to-select-option-attribute/to-select-option-attribute.pipe';
import { AppendTestIdPipe } from '../../../../pipes/append-test-id/append-test-id.pipe';
import { PopupPosition } from '../../../../directives/popup-position';
import { FilterTextsPipe } from '../../../../pipes/filterTexts/filterTexts.pipe';
import { EEntityType, ERelationType } from '@web/dtos';
import { LanguagePipe } from '../../../../pipes/language/language.pipe';

@Component({
  selector: 'aw-auto-complete',
  viewProviders: [
    {
      provide: ControlContainer,
      useExisting: FormGroupDirective,
    },
  ],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ErrorMessageComponent,
    ToSelectOptionAttributePipe,
    AppendTestIdPipe,
    PopupPosition,
    FilterTextsPipe,
    LanguagePipe,
  ],
  templateUrl: './auto-complete.component.html',
})
export class AutoCompleteComponent
  extends FormInputSelectComponent
  implements OnInit, OnDestroy
{
  public readonly useLooseValidation: InputSignal<boolean> = input(false);
  public readonly showResetIcon: InputSignal<boolean> = input(false);

  public readonly filteredOptions: WritableSignal<SelectOption[]> = signal([]);
  public readonly selectedItem: WritableSignal<number | undefined> =
    signal(undefined);
  public readonly showList: WritableSignal<boolean> = signal(false);

  protected readonly ERelationType: typeof ERelationType = ERelationType;
  protected readonly EEntityType: typeof EEntityType = EEntityType;

  constructor(public override ngControl: NgControl) {
    super(ngControl);
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.ngControl.control!.addValidators(this.isValidOption());

    this.setIndexFromValue(this.ngControl.control!.value);
    this.subscription.add(
      this.searchControl
        .valueChanges!.pipe(
          startWith(''),
          tap(value => {
            this.setIndexFromValue(value);
          }),
          map(value => this.filter(value?.toString() || '')),
          tap(filteredOptions => this.filteredOptions.set(filteredOptions))
        )
        .subscribe()
    );

    this.subscription.add(
      fromEvent<KeyboardEvent & { target?: { id: string } }>(
        document,
        'keyup'
      ).subscribe(event => {
        const isCorrectTarget = event.target?.id === this.id();
        if (isCorrectTarget) {
          event.preventDefault();
          const filteredOptions = this.filteredOptions();
          if (event.key === 'ArrowDown' && isCorrectTarget && this.showList()) {
            this.selectedItem.set(
              this.selectedItem() !== undefined
                ? this.clampSelectedItem(
                    this.selectedItem()! + 1,
                    filteredOptions.length
                  )
                : 0
            );
          } else if (
            event.key === 'ArrowUp' &&
            isCorrectTarget &&
            this.showList()
          ) {
            this.selectedItem.set(
              this.selectedItem() !== 0 && this.selectedItem() !== undefined
                ? this.clampSelectedItem(
                    this.selectedItem()! - 1,
                    filteredOptions.length
                  )
                : undefined
            );
          } else if (
            event.key === 'Enter' &&
            isCorrectTarget &&
            this.selectedItem() !== undefined &&
            this.showList() &&
            this.filteredOptions().length > 0
          ) {
            this.selectOptionAndSetItem(
              filteredOptions?.[this.selectedItem()!],
              this.selectedItem()!
            );
          }
        } else if (!isCorrectTarget && this.showList()) {
          event.preventDefault();
          this.showList.set(false);
        }
      })
    );
    this.subscription.add(
      fromEvent<MouseEvent & { target?: { id: string } }>(
        document,
        'mouseup'
      ).subscribe(event => {
        const isCorrectTarget: boolean = event?.target?.id === this.id();
        if (!isCorrectTarget) {
          this.showList.set(false);
        }
      })
    );
    if (this.ngControl.control!.value) {
      this.ngControl.control!.updateValueAndValidity();
    }
  }

  override ngOnDestroy() {
    super.ngOnDestroy();
    this.ngControl.control!.removeValidators(this.isValidOption());
  }

  public selectOptionAndSetItem(option: SelectOption, index: number): void {
    super.selectOption(option);
    this.selectedItem.set(index);
  }

  private clampSelectedItem(newValue: number, maxLength: number): number {
    if (newValue >= maxLength) {
      return maxLength - 1;
    } else if (newValue < 0) {
      return 0;
    }
    return newValue;
  }

  private filter(value: string): SelectOption[] {
    const filterValue = value?.toLowerCase();

    if (this.name()) {
      return this.options().filter(option => {
        return (option as IObjectOption)[this.name()!]
          ? ((option as IObjectOption)[this.name()!] as string)
              .toString()
              .toLowerCase()
              .includes(filterValue)
          : (option as string).toString().toLowerCase().includes(filterValue);
      });
    }

    return this.options().filter(option =>
      option?.toString()?.toLowerCase().includes(filterValue)
    );
  }

  private isValidOption(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (
        control.value === undefined ||
        control.value === null ||
        control.value === '' ||
        this.useLooseValidation()
      ) {
        return null;
      }

      const isInOptions = this.options().filter(option =>
        this.valueAttribute()
          ? control.value == (option as IObjectOption)[this.valueAttribute()!]
          : JSON.stringify(control.value) == JSON.stringify(option) ||
            control.value == option
      );

      return isInOptions.length === 0 && this.options().length !== 0
        ? { invalidOption: true }
        : null;
    };
  }

  protected openDropdownList(): void {
    this.showList.set(!this.showList());
    this.filteredOptions.set(this.options());
  }

  private setIndexFromValue(value: unknown): void {
    this.selectedItem.set(this.findIndexFromValue(value));
  }
}
